import { useMemo } from 'react'
import { useSnapshot } from 'valtio'
import useProfile from '../../../../hooks/useProfile'

/*
 * Numbers as a dashboard shows them, in the organization's language and base
 * currency — every amount the aggregates return is in base currency.
 *
 *   currency   € 125,430 — cents only below 100, where they matter
 *   integer    8,432
 *   number     104.5 — at most one decimal
 *   decimal    104.25 — two
 *   percent    42.5 % — the value is already × 100
 *
 * `compact` writes large values short (€125K, 1.2M) for axes and narrow cards.
 * `signed` puts a + on a positive value, for a change.
 */
const useAnalyticsFormat = () => {
    const { pState } = useProfile()
    const profile = useSnapshot(pState)
    const locale = profile.currentOrgLanguage || 'en-US'
    const currency = profile.currentOrgCurrency || 'DKK'

    return useMemo(() => {
        const cache = new Map()
        const nf = (opts) => {
            const key = JSON.stringify(opts)
            if (!cache.has(key)) {
                try {
                    cache.set(key, new Intl.NumberFormat(locale, opts))
                } catch {
                    cache.set(key, new Intl.NumberFormat('en-US', { ...opts, currency: opts.currency ? 'EUR' : undefined }))
                }
            }
            return cache.get(key)
        }

        const format = (value, kind = 'number', { compact = false, signed = false } = {}) => {
            if (value == null || Number.isNaN(value)) return '—'
            const sign = signed ? 'exceptZero' : 'auto'
            const small = Math.abs(value) < 100
            switch (kind) {
                case 'currency':
                    return nf({ style: 'currency', currency, signDisplay: sign, notation: compact ? 'compact' : 'standard',
                        maximumFractionDigits: compact ? 1 : small ? 2 : 0, minimumFractionDigits: compact || !small ? 0 : 2 }).format(value)
                case 'integer':
                    return nf({ signDisplay: sign, notation: compact ? 'compact' : 'standard', maximumFractionDigits: compact ? 1 : 0 }).format(value)
                case 'decimal':
                    return nf({ signDisplay: sign, notation: compact ? 'compact' : 'standard', minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(value)
                case 'percent':
                    return nf({ style: 'percent', signDisplay: sign, maximumFractionDigits: 1 }).format(value / 100)
                default:
                    return nf({ signDisplay: sign, notation: compact ? 'compact' : 'standard', maximumFractionDigits: 1 }).format(value)
            }
        }

        return { format, locale, currency }
    }, [locale, currency])
}

export default useAnalyticsFormat
